/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import request from '@/utils/request';
import type * as Type from '@/common/interface';

export const getAiGenerationConfig = () => {
  return request.get<Type.AiGenerationConfig>(
    '/answer/admin/api/ai/generation/config',
  );
};

export const saveAiGenerationConfig = (params: Type.AiGenerationConfig) => {
  return request.put('/answer/admin/api/ai/generation/config', params);
};

// "answer" runs one batch of organic-pick (random kind via PremiumRatio).
// "premium_answer" / "regular_answer" force a single answer of that kind,
// bypassing the dice roll — use for prompt debugging.
export type AiGenerationRunKind =
  | 'question'
  | 'answer'
  | 'premium_answer'
  | 'regular_answer'
  | 'tag'
  | 'bot_pool';

// AI generation manual triggers can run a full chain (LLM call + critic +
// validation), which routinely takes 20-60s for premium answers and the
// occasional 90s+ outlier. The default 10s axios timeout would abort the
// browser request long before the server finished, surfacing a misleading
// "触发失败" toast even though the work succeeded server-side. 180s gives
// us comfortable headroom and matches the backend's HTTP timeout.
export const runAiGenerationNow = (
  kind: AiGenerationRunKind,
  count?: number,
) => {
  return request.post<Type.AiGenerationRunNowResp>(
    '/answer/admin/api/ai/generation/run-now',
    { kind, count: count ?? 1 },
    { timeout: 180_000 },
  );
};

export const getAiGenerationMetrics = () => {
  return request.get<Type.AiGenerationMetrics>(
    '/answer/admin/api/ai/generation/metrics',
  );
};

export const getAiGenerationRecent = (kind?: string, verdict?: string) => {
  const params = new URLSearchParams();
  if (kind) params.set('kind', kind);
  if (verdict) params.set('verdict', verdict);
  const qs = params.toString();
  const url = qs
    ? `/answer/admin/api/ai/generation/recent?${qs}`
    : '/answer/admin/api/ai/generation/recent';
  return request.get<Type.AiGenerationRecentItem[]>(url);
};

export const setAiGenerationVerdict = (logId: number, verdict: string) => {
  return request.post('/answer/admin/api/ai/generation/verdict', {
    log_id: logId,
    verdict,
  });
};

export const suspendAllAiBots = () => {
  return request.post('/answer/admin/api/ai/generation/bots/suspend-all', {});
};

export const resumeAllAiBots = () => {
  return request.post('/answer/admin/api/ai/generation/bots/resume-all', {});
};

// flushAiGenerationCache evicts the in-process SiteInfo cache entry for
// the AI generation config so the next read goes to the DB. Use after
// raw-SQL edits or to verify the server is reading the same value the
// admin UI just saved.
export const flushAiGenerationCache = () => {
  return request.post('/answer/admin/api/ai/generation/cache/flush', {});
};
